import { Box, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import { Trans, useTranslation } from 'react-i18next';

import { InputType } from 'utils/analytics';

import { CopyChip } from './VndAnInstructions';

interface IdrWalletInstructionsProps {
  amount: string;
  currency: string;
  phone: string;
  bank: string;
  isBranded: boolean;
  onInputCopied: (inputName: InputType) => void;
}

interface CopyValue {
  value: string;
  displayedValue?: string;
  inputType: InputType;
  className?: string;
}

interface Step {
  key: string;
  copies?: CopyValue[];
}

// T-Bank: Payments → To another country → Indonesia → By phone number
export function IdrWalletInstructions({
  amount,
  currency,
  phone,
  bank,
  isBranded,
  onInputCopied,
}: IdrWalletInstructionsProps) {
  const { t } = useTranslation('payments');

  const steps: Step[] = [
    { key: 'idr_wallet_step_transfer' },
    { key: 'idr_wallet_step_country' },
    { key: 'idr_wallet_step_phone_type' },
    {
      key: 'idr_wallet_step_phone',
      copies: [
        { value: phone, inputType: 'credentials', className: 'ph-no-capture' },
      ],
    },
    {
      key: 'idr_wallet_step_bank',
      copies: [{ value: bank, inputType: 'bank' }],
    },
    {
      key: 'idr_wallet_step_pay',
      copies: [
        {
          value: amount,
          displayedValue: `${amount} ${currency}`,
          inputType: 'amount',
        },
      ],
    },
  ];

  return (
    <Box
      w='100%'
      p={['16px', '20px']}
      bg='payment.qrBackground'
      borderWidth={isBranded ? '1px' : '0'}
      borderColor='payment.qrBorder'
      borderRadius='13px'
      mt='20px'
    >
      <Text
        fontWeight='600'
        fontSize={['md', 'lg']}
        color='grey.primaryText'
        mb={['12px', '16px']}
      >
        {t('idr_wallet_instructions_title')}
      </Text>
      <VStack
        as='ol'
        align='stretch'
        spacing={['10px', '12px']}
        m='0'
        listStyleType='none'
      >
        {steps.map(({ key, copies }, index) => (
          <HStack as='li' key={key} align='flex-start' spacing='12px'>
            <Flex
              flexShrink={0}
              w='24px'
              h='24px'
              borderRadius='full'
              bg='payment.timeBar'
              color='payment.timeBarText'
              fontSize='sm'
              fontWeight='600'
              alignItems='center'
              justifyContent='center'
            >
              {index + 1}
            </Flex>
            <Box minW='0'>
              <Text
                fontWeight='400'
                fontSize={['md', 'lg']}
                color='grey.primaryText'
                lineHeight='24px'
              >
                <Trans
                  ns='payments'
                  i18nKey={key}
                  components={{
                    bold: <Text as='span' fontWeight='700' />,
                  }}
                />
              </Text>
              {copies ? (
                <Flex wrap='wrap' columnGap='8px'>
                  {copies.map((copy) => (
                    <CopyChip
                      key={copy.inputType}
                      {...copy}
                      onInputCopied={onInputCopied}
                    />
                  ))}
                </Flex>
              ) : null}
            </Box>
          </HStack>
        ))}
      </VStack>
    </Box>
  );
}
